'use client';

import { CmsHtml } from '@packages/modules/src/CmsHtml/CmsHtml.client';
import type { HtmlEditorType } from '@packages/modules/src/CmsHtml/CmsHtml.types';
import { useState } from 'react';
import { classes } from '../../utils/classes';
import { ComponentRecognizer } from '../ComponentRecognizer/ComponentRecognizer';
import type { AccordionCollapseType, CmsAccordionProps, CmsGeneralAccordionProps } from './types';

export const CmsGeneralAccordion = ({
  entries,
  highlightOpen = true,
  styleVariant = 'default',
}: CmsGeneralAccordionProps) => {
  const [openedItems, setOpenedItems] = useState<string[]>(
    // already opened items at first render
    entries?.filter((entry) => entry.isOpen).map((entry) => entry.id),
  );

  if (entries?.length && entries.length > 0) {
    const handleToggle = (itemId: string) => {
      /**
       * If there is more than one collapse opened (caused by setting multiple collapses to be open on first render),
       * we treat the Accordion as "multiple" and allow multiple Collapses to be opened.
       */
      if (openedItems.length > 1) {
        setOpenedItems((prev) =>
          prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
        );
        return;
      }
      // Else treat the Accordion as "single", therefore only one collapse can be opened at a time.

      if (openedItems.includes(itemId)) {
        // if we are closing the open collapse, set all collapses to be closed.
        setOpenedItems([]);
      } else {
        // if we are opening a collapse, set this item as the only open collapse
        setOpenedItems([itemId]);
      }
    };

    return (
      <>
        {entries.map(({ headline, id, content }) => {
          const isOpen = openedItems.includes(id);
          return (
            <div
              key={id}
              className={classes(
                'min-h-6 border-0 border-t border-solid',
                styleVariant === 'dark'
                  ? 'bg-color-footer-main border-color-footer-contrasttext'
                  : 'bg-color-background-base border-color-separator',
                highlightOpen &&
                  'border-l-4 transition-[border-color] duration-200 [&]:last-of-type:border-b',
                highlightOpen && (isOpen ? 'border-l-color-primary' : 'border-l-transparent'),
              )}
            >
              {headline && (
                <button
                  id={`header-${id}`}
                  className={classes(
                    'm-0 flex min-h-6 w-full cursor-pointer items-center justify-between px-2 text-left',
                    'sm:flex-row lg:flex-row-reverse lg:justify-end',
                    'transition-[font-weight] duration-300 ease-in-out',
                    isOpen ? 'font-bold' : 'font-normal',
                    'hover:bg-opacity-5 border-0 bg-transparent hover:bg-black',
                  )}
                  onClick={() => handleToggle(id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggle(id);
                    }
                  }}
                  aria-expanded={isOpen}
                  aria-controls={`content-${id}`}
                  type="button"
                >
                  <div className="my-1.5 flex flex-1 items-center lg:pl-2">{headline}</div>
                  <span
                    className={classes(
                      'icon-emp-cancel size-3 transition-transform duration-200',
                      styleVariant === 'dark'
                        ? 'text-color-primary-contrasttext'
                        : 'text-color-text-dark',
                      isOpen ? 'rotate-0' : 'rotate-45',
                    )}
                  />
                </button>
              )}
              <section
                id={`content-${id}`}
                className={classes(
                  // displaying as grid is a trick to animate height from 0 to auto on open and close
                  'grid overflow-hidden transition-[grid-template-rows,opacity] duration-500 ease-in-out',
                  isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
                aria-labelledby={`header-${id}`}
                aria-hidden={!isOpen}
                inert={!isOpen}
              >
                <div
                  className={classes(
                    'min-h-0 p-0 transition-[padding] duration-500 ease-in-out lg:pl-[3.5rem] [a]:decoration-0 [a]:hover:decoration-auto',
                    styleVariant === 'dark'
                      ? 'text-color-primary-contrasttext [a]:text-color-primary-contrasttext'
                      : 'text-color-text [a]:text-color-text',
                    isOpen && 'p-1.5 lg:p-1',
                  )}
                >
                  {content}
                </div>
              </section>
            </div>
          );
        })}
      </>
    );
  }

  return <div />;
};

export const MarkUpData = ({ entries }: CmsGeneralAccordionProps) => {
  if (entries?.length && entries.length > 0) {
    const structuredData = entries.map(({ headline, sanitizedContent }) => ({
      '@type': 'Question',
      name: headline,
      acceptedAnswer: {
        '@type': 'Answer',
        text: sanitizedContent,
      },
    }));

    return (
      <script key="cmsAccordionFaqMarkup" type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: structuredData,
        })}
      </script>
    );
  }

  return null;
};

export const CmsAccordion = ({ data, className }: CmsAccordionProps) => {
  const accordions = data.paragraph?.map((item: AccordionCollapseType) => ({
    id: `cms-accordion-${item.id}`,
    headline: item.field_headline,
    isOpen: item.field_is_open,
    simpleContent: item.field_html_editor,
    sanitizedContent: item.field_html_editor_sanitized,
    content: [
      item.field_html_editor && <CmsHtml key={item.id} data={item as unknown as HtmlEditorType} />,
      item.paragraph?.map((subItem) => <ComponentRecognizer item={subItem} key={subItem.id} />),
    ],
  }));

  return (
    <>
      {data?.field_use_as_seo_data && <MarkUpData entries={accordions} />}
      <div className={classes('w-full p-0', className)} style={{ margin: data?.field_margin ?? 0 }}>
        <CmsGeneralAccordion entries={accordions} />
      </div>
    </>
  );
};
